import React, { useState, useEffect } from "react";
import { collection, getDocs, orderBy, query, getDoc, doc, runTransaction } from "firebase/firestore";
import { db } from "../config/Firebase";
import { useLocation, useNavigate } from "react-router-dom";

export function Reminder() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const initialTab = searchParams.get("tab") || "patient";

  const [activeTab, setActiveTab] = useState(initialTab); // "patient" | "balance"
  const [userBranch, setUserBranch] = useState(null);

  // Sync state to URL changes
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [location.search]);

  // Handler for internal tab clicks
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/Reminder?tab=${tab}`, { replace: true });
  };
  
  const [reminderPatients, setReminderPatients] = useState([]);
  const [balanceReminders, setBalanceReminders] = useState([]);
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Settlement State
  const [isSettlementModalOpen, setIsSettlementModalOpen] = useState(false);
  const [settlementOrder, setSettlementOrder] = useState(null);
  const [settlementAmount, setSettlementAmount] = useState("");
  const [isProcessingSettlement, setIsProcessingSettlement] = useState(false);

  // 1. Fetch user branch
  useEffect(() => {
    const fetchBranch = async () => {
      try {
        const username = localStorage.getItem('currentUsername');
        if (!username) {
          setUserBranch('ADMIN');
          return;
        }

        let branchData = null;
        const ldbDoc = await getDoc(doc(db, 'LDB', username));
        if (ldbDoc.exists()) {
          branchData = ldbDoc.data().branch;
        }
        
        if (!branchData) {
          const lubDoc = await getDoc(doc(db, 'LUB', username));
          if (lubDoc.exists()) {
            branchData = lubDoc.data().branch;
          }
        }
        setUserBranch(branchData || 'ADMIN');
      } catch (error) {
        console.error("Error fetching user branch:", error);
        setUserBranch('ADMIN');
      }
    };
    fetchBranch();
  }, []);

  // 2. Fetch Reminders (Both Patient & Balance)
  useEffect(() => {
    if (userBranch === null) return; // Wait for branch

    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError("");
        
        // --- Fetch Patient Reminders ---
        const q = query(collection(db, "PatientDetail"), orderBy("visits", "desc"));
        const snapshot = await getDocs(q);
        const now = new Date();
        const pReminders = [];

        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (userBranch !== 'ADMIN' && data.branch !== userBranch) return;

          const visits = data.visits || [];
          if (visits.length > 0) {
            const lastVisit = visits[visits.length - 1];
            const lastDate = lastVisit.date.toDate();
            const diffMonths = (now.getFullYear() - lastDate.getFullYear()) * 12 + (now.getMonth() - lastDate.getMonth());
            if (diffMonths >= 6) {
              pReminders.push({
                id: docSnap.id,
                sNo: data.sNo || docSnap.id,
                name: data.name || "",
                age: data.age || "",
                sex: data.sex || "",
                branch: data.branch || "",
                address: data.address || "",
                occupation: data.occupation || "",
                lastVisitDate: lastDate.toLocaleString(),
                monthsAgo: diffMonths,
              });
            }
          }
        });
        pReminders.sort((a, b) => a.monthsAgo - b.monthsAgo);
        setReminderPatients(pReminders);

        // --- Fetch Balance Reminders ---
        const bReminders = [];
        if (userBranch === 'ADMIN') {
           const ordersSnap = await getDocs(collection(db, "local orders"));
           ordersSnap.forEach(docSnap => {
              const data = docSnap.data();
              const branchOrders = Array.isArray(data.orders) ? data.orders : [];
              branchOrders.forEach(o => {
                 if (o.balance > 0) {
                    bReminders.push({...o, branch: docSnap.id});
                 }
              });
           });
        } else {
           const branchDocSnap = await getDoc(doc(db, "local orders", userBranch));
           if (branchDocSnap.exists()) {
              const data = branchDocSnap.data();
              const branchOrders = Array.isArray(data.orders) ? data.orders : [];
              branchOrders.forEach(o => {
                 if (o.balance > 0) {
                    bReminders.push({...o, branch: userBranch});
                 }
              });
           }
        }
        
        // Sort balance reminders by date descending
        bReminders.sort((a, b) => {
           const dateA = a.date?.toDate ? a.date.toDate() : new Date(0);
           const dateB = b.date?.toDate ? b.date.toDate() : new Date(0);
           return dateB - dateA;
        });

        setBalanceReminders(bReminders);
      } catch (err) {
        setError("Error fetching reminders: " + err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [userBranch]);

  const handleCloseDeliverySubmit = async () => {
    if (!settlementOrder || !settlementAmount) return;
    setIsProcessingSettlement(true);
    setError("");

    try {
      const branchDocRef = doc(db, "local orders", settlementOrder.branch);
      const paymentAmount = Number(settlementAmount);

      await runTransaction(db, async (transaction) => {
        const branchDoc = await transaction.get(branchDocRef);
        if (!branchDoc.exists()) {
          throw new Error("Branch document not found");
        }

        const data = branchDoc.data();
        const ordersArray = Array.isArray(data.orders) ? data.orders : [];

        const updatedOrders = ordersArray.map(order => {
          if ((order.orderId || order.mobile) === (settlementOrder.orderId || settlementOrder.mobile)) {
            const currentTotal = Number(order.total || 0);
            const currentAdvance = Number(order.advance || 0);
            
            // Log the payment
            const newPaymentHistory = [...(order.paymentHistory || [])];
            newPaymentHistory.push({
              amount: paymentAmount,
              date: new Date(),
              type: 'settlement_payment'
            });

            const newAdvance = currentAdvance + paymentAmount;
            let newTotal = currentTotal;
            let extraDiscount = 0;

            if (paymentAmount < (order.balance || 0)) {
               // They are paying less than the balance. Apply discount.
               extraDiscount = (order.balance || 0) - paymentAmount;
               newTotal = currentTotal - extraDiscount;
               
               if (!order.discount) order.discount = { amount: 0 };
               order.discount.amount = Number(order.discount.amount || 0) + extraDiscount;
               
               const discountLog = [...(order.additionalDiscountLog || [])];
               discountLog.push({
                 amount: extraDiscount,
                 date: new Date(),
                 reason: "Settlement adjustment"
               });
               order.additionalDiscountLog = discountLog;
            }

            return {
              ...order,
              total: newTotal,
              advance: newAdvance,
              balance: 0, // Fully settled
              paymentHistory: newPaymentHistory,
              status: "closed", 
              updatedAt: new Date()
            };
          }
          return order;
        });

        transaction.update(branchDocRef, { orders: updatedOrders });
      });

      setIsSettlementModalOpen(false);
      setSettlementOrder(null);
      setSettlementAmount("");
      
      // Update local state instead of waiting for poll
      setBalanceReminders(prev => prev.filter(o => o.id !== settlementOrder.id));

    } catch (err) {
      setError("Settlement Error: " + err.message);
    } finally {
      setIsProcessingSettlement(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <p>Loading reminders...</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto my-10 p-8 bg-white rounded-xl shadow-lg">
      <h1 className="text-3xl font-bold text-gray-800 text-center mb-6">Reminders</h1>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6 justify-center gap-4">
        <button
          onClick={() => handleTabChange("patient")}
          className={`py-2 px-6 font-semibold text-lg transition-colors ${
            activeTab === "patient"
              ? "border-b-4 border-black text-black"
              : "text-gray-500 hover:text-black"
          }`}
        >
          Patient Reminders
        </button>
        <button
          onClick={() => handleTabChange("balance")}
          className={`py-2 px-6 font-semibold text-lg transition-colors ${
            activeTab === "balance"
              ? "border-b-4 border-black text-black"
              : "text-gray-500 hover:text-black"
          }`}
        >
          Balance Reminders
        </button>
      </div>

      {error && (
        <div className="bg-red-100 text-red-700 px-4 py-2 rounded mb-6 text-center">
          {error}
        </div>
      )}

      {/* PATIENT REMINDERS TAB */}
      {activeTab === "patient" && (
        <div>
          <h2 className="text-xl font-semibold mb-4 text-gray-700">Patients (6+ Months Post-Visit)</h2>
          {reminderPatients.length === 0 ? (
            <div className="text-center py-10 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-gray-500 text-lg font-medium">No patient reminders at this time.</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full bg-white text-sm">
                <thead>
                  <tr className="bg-gray-100 text-gray-600 uppercase text-xs leading-normal">
                    <th className="px-4 py-3 text-left">S.No</th>
                    <th className="px-4 py-3 text-left">Name</th>
                    <th className="px-4 py-3 text-left">Age/Sex</th>
                    <th className="px-4 py-3 text-left">Branch</th>
                    <th className="px-4 py-3 text-left">Last Visit</th>
                    <th className="px-4 py-3 text-left">Months Ago</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700 font-medium">
                  {reminderPatients.map((patient, idx) => (
                    <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3">{patient.sNo}</td>
                      <td className="px-4 py-3 text-black">{patient.name}</td>
                      <td className="px-4 py-3">{patient.age} / {patient.sex}</td>
                      <td className="px-4 py-3">
                         <span className="bg-gray-200 text-gray-700 py-1 px-3 rounded-full text-xs">{patient.branch}</span>
                      </td>
                      <td className="px-4 py-3">{patient.lastVisitDate}</td>
                      <td className="px-4 py-3 text-red-600 font-bold">{patient.monthsAgo} months</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* BALANCE REMINDERS TAB */}
      {activeTab === "balance" && (
        <div>
          <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
            <h2 className="text-xl font-semibold text-gray-700">Pending Balances</h2>
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black bg-white shadow-sm"
              >
                <option value="all">All Statuses</option>
                <option value="order booked">Order Booked</option>
                <option value="order ready">Order Ready</option>
                <option value="order delivered">Order Delivered</option>
              </select>
              <div className="relative w-full sm:w-64 md:w-72">
                <input
                  type="text"
                  placeholder="Search by name or phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black transition-shadow shadow-sm"
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          </div>

          {balanceReminders.filter(order => {
            const query = searchQuery.toLowerCase();
            const matchesSearch = (order.patientName || "").toLowerCase().includes(query) || (order.mobile || "").includes(query);
            const matchesStatus = statusFilter === "all" || order.status === statusFilter;
            return matchesSearch && matchesStatus;
          }).length === 0 ? (
            <div className="text-center py-10 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-gray-500 text-lg font-medium">No pending balances found matching your criteria.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {balanceReminders.filter(order => {
                const query = searchQuery.toLowerCase();
                const matchesSearch = (order.patientName || "").toLowerCase().includes(query) || (order.mobile || "").includes(query);
                const matchesStatus = statusFilter === "all" || order.status === statusFilter;
                return matchesSearch && matchesStatus;
              }).map((order, idx) => (
                <div 
                  key={idx} 
                  onClick={() => { setSelectedOrder(order); setIsModalOpen(true); }}
                  className="flex flex-col p-5 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all cursor-pointer relative overflow-hidden group"
                >
                  <div className="absolute top-0 left-0 w-1 h-full bg-red-500"></div>
                  <div className="flex justify-between items-start mb-3 pl-2">
                    <div>
                      <h3 className="font-bold text-lg text-gray-900 group-hover:text-blue-600 transition-colors">{order.patientName || "Walk-in"}</h3>
                      <p className="text-sm text-gray-500 font-medium">{order.mobile}</p>
                    </div>
                    <span className="bg-red-50 text-red-700 py-1 px-3 rounded-full text-sm font-bold whitespace-nowrap border border-red-100 shadow-sm">
                      Bal: ₹{(order.balance || 0).toFixed(2)}
                    </span>
                  </div>
                  
                  <div className="space-y-2 mb-4 text-sm pl-2">
                    <div className="flex justify-between items-center bg-gray-50 p-2 rounded">
                      <span className="text-gray-500 text-xs uppercase tracking-wider font-semibold">Bill No</span>
                      <span className="font-medium text-gray-800">{order.billNumber || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center px-2">
                      <span className="text-gray-500">Status</span>
                      <span className="font-medium capitalize text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-100">{order.status || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center px-2">
                      <span className="text-gray-500">Date</span>
                      <span className="font-medium">{order.date?.toDate ? order.date.toDate().toLocaleDateString() : 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center px-2">
                      <span className="text-gray-500">Total</span>
                      <span className="font-medium">₹{(order.total || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center px-2">
                      <span className="text-gray-500">Advance</span>
                      <span className="font-medium text-green-600">₹{(order.advance || 0).toFixed(2)}</span>
                    </div>
                  </div>
                  
                  {order.deliveryDescription && (
                    <div className="mb-4 pl-2">
                       <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                         <p className="text-xs font-semibold text-blue-800 mb-1 uppercase tracking-wider">Delivery Notes</p>
                         <p className="text-sm text-blue-900 line-clamp-2">{order.deliveryDescription}</p>
                       </div>
                    </div>
                  )}
                  
                  <div className="mt-auto pt-3 border-t border-gray-100 flex justify-between items-center pl-2">
                     <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-md font-medium border border-gray-200">{order.branch}</span>
                     <div className="flex gap-3 items-center">
                       {order.status === "order delivered" && (
                         <button 
                           onClick={(e) => { e.stopPropagation(); setSettlementOrder(order); setIsSettlementModalOpen(true); }}
                           className="text-xs font-bold text-white bg-green-600 hover:bg-green-700 px-3 py-1.5 rounded shadow-sm transition-colors"
                         >
                            Close Delivery
                         </button>
                       )}
                       <span className="text-sm font-semibold text-blue-600 flex items-center gap-1 group-hover:underline">
                          Details →
                       </span>
                     </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* COMPREHENSIVE DETAILS MODAL */}
      {isModalOpen && selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden animate-in fade-in zoom-in duration-200">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{selectedOrder.patientName || "Walk-in"}</h3>
                <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                  <span className="font-medium bg-gray-200 px-2 py-0.5 rounded text-gray-700">Bill: {selectedOrder.billNumber || 'N/A'}</span>
                  <span>{selectedOrder.mobile}</span>
                  <span className="capitalize px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-medium">{selectedOrder.status}</span>
                </div>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-200 text-gray-500 hover:bg-gray-300 hover:text-gray-800 transition-colors"
              >
                ✕
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1 space-y-6 bg-white">
              
              {/* Financial & Delivery Split */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* Financial Summary */}
                <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                  <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 border-b pb-2">Financial Breakdown</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">Order Total</span>
                      <span className="font-medium text-gray-900">₹{(selectedOrder.total || 0).toFixed(2)}</span>
                    </div>
                    {selectedOrder.discount?.amount > 0 && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Total Discount</span>
                        <span className="font-medium text-purple-600">- ₹{(selectedOrder.discount.amount || 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">Advance Paid</span>
                      <span className="font-medium text-green-600">₹{(selectedOrder.advance || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                      <span className="font-bold text-gray-800">Balance Due</span>
                      <span className="text-lg font-extrabold text-red-600">₹{(selectedOrder.balance || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Delivery & Logistics */}
                <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                   <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 border-b pb-2">Logistics</h4>
                   <div className="space-y-3">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Branch</span>
                        <span className="font-medium text-gray-900">{selectedOrder.branch}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Order Date</span>
                        <span className="font-medium">{selectedOrder.date?.toDate ? selectedOrder.date.toDate().toLocaleDateString() : 'N/A'}</span>
                      </div>
                      {selectedOrder.deliveredDate && (
                         <div className="flex justify-between items-center text-sm">
                           <span className="text-gray-600">Delivered On</span>
                           <span className="font-medium">{selectedOrder.deliveredDate?.toDate ? selectedOrder.deliveredDate.toDate().toLocaleDateString() : 'N/A'}</span>
                         </div>
                      )}
                   </div>
                   {selectedOrder.deliveryDescription && (
                     <div className="mt-4 pt-3 border-t border-gray-100">
                       <span className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Delivery Notes</span>
                       <p className="text-sm text-gray-800 bg-gray-50 p-2 rounded border border-gray-100">{selectedOrder.deliveryDescription}</p>
                     </div>
                   )}
                </div>

              </div>

              {/* Ordered Products Section */}
              {selectedOrder.orderItems && selectedOrder.orderItems.length > 0 && (
                <div>
                  <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">Products Ordered</h4>
                  <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Type</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Product</th>
                          <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Qty</th>
                          <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Price</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedOrder.orderItems.map((item, idx) => (
                          <tr key={idx} className="bg-white">
                            <td className="px-4 py-3 font-medium text-gray-900 capitalize">{item.type}</td>
                            <td className="px-4 py-3 text-gray-600">
                              {item.name || item.brand || 'Custom Item'}
                              {item.model && <span className="text-xs text-gray-400 block">{item.model}</span>}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-900">{item.quantity || 1}</td>
                            <td className="px-4 py-3 text-right font-medium text-gray-900">₹{((item.cost || item.price || 0) * (item.quantity || 1)).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Payment History Log */}
              {selectedOrder.paymentHistory && selectedOrder.paymentHistory.length > 0 && (
                 <div>
                   <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">Payment History</h4>
                   <div className="space-y-2">
                     {selectedOrder.paymentHistory.map((payment, idx) => (
                        <div key={idx} className="flex justify-between items-center text-sm bg-green-50 p-3 rounded-lg border border-green-100">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500"></span>
                            <span className="text-green-800 font-medium capitalize">{payment.type ? payment.type.replace('_', ' ') : 'Payment'}</span>
                          </div>
                          <div className="text-right">
                            <span className="block font-bold text-green-700">₹{(payment.amount || 0).toFixed(2)}</span>
                            <span className="text-xs text-green-600">{payment.date?.toDate ? payment.date.toDate().toLocaleDateString() : 'N/A'}</span>
                          </div>
                        </div>
                     ))}
                   </div>
                 </div>
              )}

            </div>
            
            {/* Modal Footer */}
            <div className="p-5 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 rounded-b-2xl">
              {selectedOrder.mobile && (
                <a 
                  href={`tel:${selectedOrder.mobile}`}
                  className="px-6 py-2.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors shadow-sm flex items-center gap-2"
                >
                  Call Patient
                </a>
              )}
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-6 py-2.5 bg-gray-800 text-white font-medium rounded-lg hover:bg-gray-900 transition-colors shadow-sm"
              >
                Close
              </button>
            </div>
            
          </div>
        </div>
      )}

      {/* SETTLEMENT MODAL */}
      {isSettlementModalOpen && settlementOrder && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-5 border-b border-gray-100 bg-gray-50">
              <h3 className="text-xl font-bold text-gray-900">Close Delivery Account</h3>
              <p className="text-sm text-gray-500 mt-1">Settle the remaining balance for {settlementOrder.patientName || "Walk-in"}</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex justify-between items-center bg-red-50 p-4 rounded-lg border border-red-100">
                <span className="font-semibold text-red-800">Current Balance Due:</span>
                <span className="text-xl font-extrabold text-red-600">₹{(settlementOrder.balance || 0).toFixed(2)}</span>
              </div>
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Closing Amount Collected (₹)</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-4 font-bold text-gray-500">₹</span>
                  <input
                    type="number"
                    placeholder="Enter amount..."
                    value={settlementAmount}
                    onChange={(e) => setSettlementAmount(e.target.value)}
                    className="w-full pl-8 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black font-semibold text-lg"
                  />
                </div>
                {Number(settlementAmount) > 0 && Number(settlementAmount) < (settlementOrder.balance || 0) && (
                  <p className="text-xs text-orange-600 font-semibold mt-2">
                    Note: This is less than the balance. A discount of ₹{((settlementOrder.balance || 0) - Number(settlementAmount)).toFixed(2)} will be applied and the account will be closed.
                  </p>
                )}
              </div>
            </div>
            
            <div className="p-5 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => { setIsSettlementModalOpen(false); setSettlementAmount(""); }}
                className="px-5 py-2.5 text-gray-600 font-semibold hover:bg-gray-200 rounded-lg transition-colors"
                disabled={isProcessingSettlement}
              >
                Cancel
              </button>
              <button
                onClick={handleCloseDeliverySubmit}
                disabled={!settlementAmount || isProcessingSettlement}
                className="px-6 py-2.5 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm flex items-center gap-2"
              >
                {isProcessingSettlement ? "Processing..." : "Confirm & Close"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}